/**
 * Reusable photo upload slots (same as EQ tab in RoomEqForm).
 * Used by RoomEqForm and VenueScreen RF tab.
 */
import React from 'react';
import { View, Text, TouchableOpacity, TextInput, Image, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

export function PhotoSlotsUpload({ photos, onPhotosChange, maxPhotos = 5 }) {
  const addSlot = () => {
    if (photos.length >= maxPhotos) return;
    onPhotosChange((prev) => [...prev, { file: null, preview: null, description: '' }]);
  };

  const removeSlot = (index) => {
    if (photos.length <= 1) return;
    onPhotosChange((prev) => prev.filter((_, i) => i !== index));
  };

  const setPhotoFile = (index, file) => {
    let preview = null;
    if (file) {
      if (file.uri) preview = file.uri;
      else if (typeof URL !== 'undefined' && URL.createObjectURL) preview = URL.createObjectURL(file);
    }
    onPhotosChange((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], file, preview };
      return next;
    });
  };

  const setPhotoDescription = (index, description) => {
    onPhotosChange((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], description };
      return next;
    });
  };

  const handleFileChange = (index, e) => {
    const file = e?.target?.files?.[0] || e?.nativeEvent?.target?.files?.[0];
    if (file) setPhotoFile(index, file);
  };

  const renderCameraAction = (index) => (
    <label style={styles.cameraLabel}>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => handleFileChange(index, e)}
      />
      <Ionicons name="camera-outline" size={18} color="#000" />
      <Text style={styles.changeLink}>Take photo</Text>
    </label>
  );

  return (
    <>
      {photos.map((slot, index) => (
        <View key={index} style={styles.photoCard}>
          {slot.preview ? (
            <>
              <View style={styles.previewWrap}>
                <Image source={{ uri: slot.preview }} style={styles.previewImage} resizeMode="cover" />
              </View>
              <View style={styles.slotActions}>
                {Platform.OS === 'web' ? (
                  <label style={styles.changeLabel}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(index, e)}
                    />
                    <Text style={styles.changeLink}>Change photo</Text>
                  </label>
                ) : (
                  <TouchableOpacity onPress={() => {}} style={styles.changeLabel}>
                    <Text style={styles.changeLink}>Change photo</Text>
                  </TouchableOpacity>
                )}
                {Platform.OS === 'web' && renderCameraAction(index)}
                <TouchableOpacity
                  onPress={() => removeSlot(index)}
                  disabled={photos.length <= 1}
                  style={styles.removeBtn}
                >
                  <Ionicons name="trash-outline" size={18} color="#c00" />
                  <Text style={styles.removeBtnText}>Remove</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            Platform.OS === 'web' ? (
              <>
                <label style={styles.placeholder}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(index, e)}
                  />
                  <View style={styles.placeholderInner}>
                    <Ionicons name="image-outline" size={40} color="#aaa" />
                    <Text style={styles.placeholderText}>Photo {index + 1}</Text>
                    <Text style={styles.uploadLink}>Tap to choose image</Text>
                  </View>
                </label>
                {renderCameraAction(index)}
              </>
            ) : (
              <TouchableOpacity style={styles.placeholder} activeOpacity={0.8} onPress={() => {}}>
                <View style={styles.placeholderInner}>
                  <Ionicons name="image-outline" size={40} color="#aaa" />
                  <Text style={styles.placeholderText}>Photo {index + 1}</Text>
                  <Text style={styles.uploadLink}>Tap to choose image</Text>
                </View>
              </TouchableOpacity>
            )
          )}
          <TextInput
            style={styles.descInput}
            placeholder="Description for this photo (optional)"
            placeholderTextColor="#000"
            value={slot.description}
            onChangeText={(t) => setPhotoDescription(index, t)}
            multiline
            numberOfLines={2}
          />
        </View>
      ))}
      {photos.length < maxPhotos && (
        <TouchableOpacity onPress={addSlot} style={styles.addSlot}>
          <Ionicons name="add-circle-outline" size={28} color="#424252" />
          <Text style={styles.addSlotText}>Add another photo</Text>
        </TouchableOpacity>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  photoCard: { marginBottom: 24 },
  previewWrap: {
    width: '100%',
    aspectRatio: 4 / 3,
    maxHeight: 280,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  previewImage: { width: '100%', height: '100%' },
  placeholder: {
    width: '100%',
    minHeight: 160,
    borderRadius: 12,
    backgroundColor: '#fafafa',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
    ...(Platform.OS === 'web' && { cursor: 'pointer', display: 'block' }),
  },
  placeholderInner: { alignItems: 'center', justifyContent: 'center', padding: 24 },
  placeholderText: { fontSize: 20, color: '#000', marginTop: 8, fontFamily: 'Computer Says No' },
  uploadLink: { fontSize: 20, color: '#000', marginTop: 4, fontFamily: 'Computer Says No' },
  slotActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
    marginBottom: 10,
  },
  changeLabel: {},
  cameraLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    minHeight: 44,
    marginBottom: 10,
  },
  changeLink: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  removeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 0 },
  removeBtnText: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
  descInput: {
    borderWidth: 1,
    borderColor: '#000',
    borderRadius: 8,
    padding: 12,
    fontSize: 20,
    color: '#000',
    minHeight: 48,
    textAlignVertical: 'top',
    fontFamily: 'Computer Says No',
  },
  addSlot: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  addSlotText: { fontSize: 20, color: '#000', fontFamily: 'Computer Says No' },
});
